import { dayjs } from "@thalamos/common";
import type {
  Section12PaymentRequestItem,
  Section12PaymentRequestStatus,
  Section12PaymentRequestWithDetails,
} from "@thalamos/aspire-common";

/**
 * Determine S12 accreditation status from an expiry date.
 * Returns `"accredited"`, `"expired"`, or `null` when no date is available.
 */
export function getS12AccreditationStatus(
  expiryDate: string | null | undefined,
): "accredited" | "expired" | null {
  if (!expiryDate) {
    return null;
  }

  return dayjs(expiryDate).isBefore(dayjs(), "day") ? "expired" : "accredited";
}

export const UNKNOWN_LOCAL_AUTHORITY_LABEL = "Unknown LA";

export const STATUS_LABELS: Record<Section12PaymentRequestStatus, string> = {
  pending: "Pending AMHP approval",
  accepted: "Approved by AMHP",
  rejected: "Rejected by AMHP",
  paid: "Paid",
  "payment-processing": "Payment Processing",
  "payment-on-hold": "Payment on hold",
  "receipt-missing": "Receipt Missing",
  "bank-information-missing": "Bank Information Missing",
  "value-incorrect": "Value Incorrect",
  "registration-with-icb-needed": "Registration with ICB needed",
  deleted: "Deleted",
};

export const STATUS_LABELS_ADMIN: Record<
  Section12PaymentRequestStatus,
  string
> = {
  pending: "Pending AMHP approval",
  accepted: "Approved by AMHP",
  rejected: "Rejected by AMHP",
  paid: "Paid",
  "payment-processing": "Payment Processing",
  "payment-on-hold": "Payment on hold",
  "receipt-missing": "Receipt Missing",
  "bank-information-missing": "Bank Information Missing",
  "value-incorrect": "Value Incorrect",
  "registration-with-icb-needed": "Registration with ICB needed",
  deleted: "Deleted",
};

export const STATUS_COLORS: Record<
  Section12PaymentRequestStatus,
  "default" | "warning" | "success" | "error" | "info"
> = {
  pending: "warning",
  accepted: "info",
  rejected: "error",
  paid: "success",
  // Neutral base; the visible fill comes from STATUS_CHIP_SX.
  "payment-processing": "default",
  "payment-on-hold": "error",
  "receipt-missing": "error",
  "bank-information-missing": "error",
  "value-incorrect": "error",
  "registration-with-icb-needed": "error",
  deleted: "default",
};

/**
 * Chip fills for statuses no MUI semantic colour expresses. Processing is the
 * step immediately before payment, so it takes a lighter green than `paid`.
 */
export const STATUS_CHIP_SX: Partial<
  Record<Section12PaymentRequestStatus, { bgcolor: string; color: string }>
> = {
  "payment-processing": {
    bgcolor: "chip.processingBackground",
    color: "text.primary",
  },
};

export function formatPaymentDate(date: string | null | undefined): string {
  if (!date) return "—";
  return dayjs(date).format("DD/MM/YYYY HH:mm");
}

export function formatCurrency(amountPence: number | null | undefined): string {
  if (amountPence == null) return "—";
  return `£${(amountPence / 100).toFixed(2)}`;
}

export function getItemTypeLabel(item: Section12PaymentRequestItem): string {
  switch (item.itemType) {
    case "configured-rate":
      return item.configuredRateLabel ?? "Configured Rate";
    case "mileage":
      return "Mileage";
    case "other":
      return "Other";
    default:
      return item.itemType;
  }
}

/**
 * Format a doctor's display name, appending their team name in brackets
 * when they are not on an independent team.
 */
export function formatDoctorDisplayName(
  name: string,
  teamName: string | null | undefined,
  teamType: string | null | undefined,
): string {
  if (teamType && teamType !== "independent" && teamName) {
    return `${name} (${teamName})`;
  }
  return name;
}

export function downloadCsvBlob(blob: Blob, fileName: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export function buildCsvFileName(
  prefix: string,
  fromDate: string | undefined,
  toDate: string | undefined,
): string {
  const fromDatePart = fromDate || "all";
  const toDatePart = toDate || "now";
  return `${prefix}-${fromDatePart}-to-${toDatePart}.csv`;
}

export type PaymentRequestColumn =
  "doctor" | "amhpTeam" | "localAuthority" | "total" | "status" | "created";

export const ALL_COLUMNS: PaymentRequestColumn[] = [
  "doctor",
  "amhpTeam",
  "localAuthority",
  "total",
  "status",
  "created",
];

export const COLUMN_LABELS: Record<PaymentRequestColumn, string> = {
  doctor: "Doctor",
  amhpTeam: "AMHP Team",
  localAuthority: "Local Authority",
  total: "Total",
  status: "Status",
  created: "Created",
};

export function getCellValue(
  request: Section12PaymentRequestWithDetails,
  column: PaymentRequestColumn,
): string | null {
  switch (column) {
    case "doctor":
      return request.doctor?.name ?? "Unknown Doctor";
    case "amhpTeam":
      return request.amhpTeam?.name ?? "Unknown Team";
    case "localAuthority":
      return request.localAuthority?.name ?? UNKNOWN_LOCAL_AUTHORITY_LABEL;
    case "total":
      return formatCurrency(request.totalAmountPence);
    case "created":
      return formatPaymentDate(request.created);
    default:
      return null;
  }
}
